// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import React, { useEffect, useState, useRef } from 'react';
import './StatsOverlay.css';

interface NetworkStats {
  rtt?: number;
  jitter?: number;
  bitrate?: string;
}

interface StatsOverlayProps {
  gameliftstreams?: any;
  perfStats?: any;
  isVisible?: boolean;
  isStreamRunning?: boolean;
}

const StatsOverlay: React.FC<StatsOverlayProps> = ({
  gameliftstreams,
  perfStats = {},
  isVisible = false,
  isStreamRunning = false
}) => {
  const [networkStats, setNetworkStats] = useState<NetworkStats>({});
  const [frameRate, setFrameRate] = useState<number | undefined>(undefined);
  const statsIntervalRef = useRef<NodeJS.Timer | null>(null);
  const previousBytesReceivedRef = useRef<number>(0);
  const previousTimestampRef = useRef<number>(0);

  const startStatsLogging = () => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
    }

    statsIntervalRef.current = setInterval(async () => {
      if (gameliftstreams && isVisible) {
        try {
          const stats = await gameliftstreams.getVideoRTCStats();
          processStats(stats);
        } catch {
          // Stats unavailable
        }
      }
    }, 1000);
  };

  const stopStatsLogging = () => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }
  };

  const processStats = (stats: any) => {
    if (!previousBytesReceivedRef.current) {
      previousBytesReceivedRef.current = 0;
      previousTimestampRef.current = Date.now();
    }

    const newNetworkStats = { ...networkStats };
    let newFrameRate = frameRate;

    stats.forEach((report: any) => {
      if (report.type === 'inbound-rtp' && report.framesPerSecond !== undefined) {
        newFrameRate = Math.round(report.framesPerSecond);
        newNetworkStats.jitter = report.jitter ? Math.round(report.jitter * 1000) : undefined;

        if (report.bytesReceived && previousBytesReceivedRef.current) {
          const now = Date.now();
          const timeDiff = (now - previousTimestampRef.current) / 1000;
          const bytesDiff = report.bytesReceived - previousBytesReceivedRef.current;
          const bitrate = ((bytesDiff * 8) / timeDiff / 1000000).toFixed(2);
          newNetworkStats.bitrate = bitrate;
          previousTimestampRef.current = now;
        }
        previousBytesReceivedRef.current = report.bytesReceived || 0;
      }
      
      if (report.type === 'candidate-pair' && report.state === 'succeeded') {
        newNetworkStats.rtt = report.currentRoundTripTime ? Math.round(report.currentRoundTripTime * 1000) : undefined;
      }
    });

    setNetworkStats(newNetworkStats);
    setFrameRate(newFrameRate);
  };

  useEffect(() => {
    return () => {
      stopStatsLogging();
    };
  }, []);

  useEffect(() => {
    if (isVisible && isStreamRunning) {
      startStatsLogging();
    } else {
      stopStatsLogging();
    }
    // eslint-disable-next-line
  }, [isVisible, isStreamRunning, gameliftstreams]);

  const createStatBar = (
    value: any,
    maxValue = 2.0,
    threshold = 1.0,
    showDivider = true,
    suffix = '',
    secondaryText = ''
  ): React.ReactElement => {
    if (value === '-' || value === undefined || value === null || isNaN(parseFloat(value))) {
      const divider = showDivider ? <div className="bar-divider"></div> : null;
      const secondary = secondaryText ? <span className="stats-secondary">{secondaryText}</span> : null;
      return (
        <>
          <div className="bar-container">{divider}</div>
          <span>{value || '-'}{suffix}{secondary && ' '}{secondary}</span>
        </>
      );
    }
    
    const numValue = parseFloat(value);
    const percentage = Math.min((numValue / maxValue) * 100, 100);
    const isGood = numValue <= threshold;
    const fillClass = isGood ? 'good' : 'bad';
    const divider = showDivider ? <div className="bar-divider"></div> : null;
    const secondary = secondaryText ? <span className="stats-secondary">{secondaryText}</span> : null;
    const formattedValue = numValue.toFixed(2);
    
    return (
      <>
        <div className="bar-container">
          <div className={`bar-fill ${fillClass}`} style={{ width: `${percentage}%` }}></div>
          {divider}
        </div>
        <span>{formattedValue}{suffix}{secondary && ' '}{secondary}</span>
      </>
    );
  };

  const formatMBtoGB = (mbValue: any): string => {
    const num = parseFloat(mbValue);
    return isNaN(num) ? '' : `${(num / 1024).toFixed(2)}G`;
  };

  const fps = frameRate || '-';

  if (!isStreamRunning) {
    return null;
  }

  return (
    <div className={`stats-overlay ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="stats-head"><span className="stats-section">Client FPS</span><span className="stats-fps">{fps}</span></div>
      <div className="stats-rule"></div>
      
      <div className="stats-section">Application · normalized</div>
      <div className="stat-line">
        <span>CPU</span>
        {createStatBar(perfStats.application?.cpuUtilizationNormalized, 1.0, 1.0, false)}
      </div>
      <div className="stat-line">
        <span>MEM</span>
        {createStatBar(
          perfStats.application?.memoryUtilizationNormalized, 
          1.0, 
          1.0, 
          false, 
          '', 
          formatMBtoGB(perfStats.application?.memoryUtilizationMb)
        )}
      </div>
      
      
      
      <div className="stats-section">Instance</div>
      <div className="stat-line">
        <span>CPU</span>
        {createStatBar(perfStats.system?.cpuUtilization, 100, 90, false, '%')}
      </div>
      <div className="stat-line">
        <span>MEM</span>
        {createStatBar(
          perfStats.system?.memoryUtilization, 
          100, 
          90, 
          false, 
          '%', 
          formatMBtoGB(perfStats.system?.memoryUtilizationMb)
        )}
      </div>
      <div className="stat-line">
        <span>GPU</span>
        {createStatBar(perfStats.system?.gpuUtilization, 100, 90, false, '%')}
      </div>
      <div className="stat-line">
        <span>VRAM</span>
        {createStatBar(
          perfStats.system?.vramUtilization, 
          100, 
          90, 
          false, 
          '%', 
          formatMBtoGB(perfStats.system?.vramUtilizationMb)
        )}
      </div>
      
      
      
      <div className="stats-section">Network</div>
      <div className="stat-line">
        <span>RTT</span>
        <span></span>
        <span>{networkStats.rtt || '-'} ms</span>
      </div>
      <div className="stat-line">
        <span>Jitter</span>
        <span></span>
        <span>{networkStats.jitter || '-'} ms</span>
      </div>
      <div className="stat-line">
        <span>Bitrate</span>
        <span></span>
        <span>{networkStats.bitrate || '-'} mbps</span>
      </div>
    </div>
  );
};

export default StatsOverlay;
